'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import AnnouncementModal, { type AnnouncementItem } from './AnnouncementModal'

type ActiveResponse =
  | { ok: true; show: false; reason?: string; gateType?: string }
  | { ok: true; show: true; item: AnnouncementItem }
  | { ok: false; error?: string }

async function postJson(url: string, body: Record<string, any>) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  let json: any = null
  try {
    json = await res.json()
  } catch {}

  if (!res.ok) {
    throw new Error(json?.error || 'Request failed')
  }

  return json
}

function isExternalUrl(url: string) {
  return /^https?:\/\//i.test(url)
}

export default function AnnouncementLauncher() {
  const router = useRouter()
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [item, setItem] = useState<AnnouncementItem | null>(null)

  const viewedRef = useRef<number | null>(null)
  const closedRef = useRef<number | null>(null)
  const clickedRef = useRef<number | null>(null)
  const loadingRef = useRef(false)

  const loadActiveAnnouncement = useCallback(async () => {
    if (loadingRef.current) return

    try {
      loadingRef.current = true
      setLoading(true)

      const res = await fetch('/api/announcements/active', {
        method: 'GET',
        cache: 'no-store',
      })

      const json: ActiveResponse = await res.json()

      if (!res.ok || !json?.ok) return

      if (!('show' in json) || !json.show || !json.item) {
        setItem(null)
        setOpen(false)
        return
      }

      setItem(json.item)
      setOpen(true)
    } catch (err) {
      console.error('AnnouncementLauncher load error:', err)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadActiveAnnouncement()
  }, [loadActiveAnnouncement])

  useEffect(() => {
    function handleCommunityJoined() {
      loadActiveAnnouncement()
    }

    window.addEventListener('hubee:community-joined', handleCommunityJoined)
    window.addEventListener('community:joined', handleCommunityJoined)

    return () => {
      window.removeEventListener('hubee:community-joined', handleCommunityJoined)
      window.removeEventListener('community:joined', handleCommunityJoined)
    }
  }, [loadActiveAnnouncement])

  useEffect(() => {
    if (!open || !item?.announcement_id) return
    if (viewedRef.current === item.announcement_id) return

    viewedRef.current = item.announcement_id

    postJson('/api/announcements/view', {
      announcement_id: item.announcement_id,
    }).catch((err) => {
      console.error('Announcement view error:', err)
    })
  }, [open, item])

  async function handleClose() {
    if (!item?.announcement_id) return
    if (closedRef.current === item.announcement_id) return

    closedRef.current = item.announcement_id

    try {
      await postJson('/api/announcements/close', {
        announcement_id: item.announcement_id,
      })
    } catch (err) {
      console.error('Announcement close error:', err)
    }
  }

  async function handleAction(current: AnnouncementItem) {
    if (!current?.announcement_id) return

    if (clickedRef.current !== current.announcement_id) {
      clickedRef.current = current.announcement_id

      try {
        await postJson('/api/announcements/click', {
          announcement_id: current.announcement_id,
        })
      } catch (err) {
        console.error('Announcement click error:', err)
      }
    }

    const href = (current.button_link || '').trim()

    if (current.action === 'close' || !href) {
      setOpen(false)
      await handleClose()
      return
    }

    setOpen(false)

    if (current.action === 'product' && href) {
      router.push(href)
      return
    }

    if (current.action === 'link' && href) {
      if (isExternalUrl(href)) {
        window.location.href = href
      } else {
        router.push(href)
      }
    }
  }

  if (loading && !item) return null
  if (!item) return null

  return (
    <AnnouncementModal
      open={open}
      item={item}
      onOpenChange={async (nextOpen) => {
        setOpen(nextOpen)

        if (!nextOpen) {
          await handleClose()
        }
      }}
      onClose={handleClose}
      onAction={handleAction}
    />
  )
}